import type { EvaluationResults, LlmUsage } from "../types/EvaluationResults";
import { LLM_PRICING } from "../types/llm_pricing.type";

export type CostSource = "reported" | "estimated" | "mixed" | "unavailable";
const nonnegative = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
const probability = (value: unknown) => {
  const n = nonnegative(value);
  return n !== null && n <= 1 ? n : null;
};
const sumComplete = (values: (number | null)[]) =>
  values.length && values.every((v) => v !== null)
    ? values.reduce<number>((sum, v) => sum + (v ?? 0), 0) : null;
const costSource = (sources: CostSource[]): CostSource =>
  sources.includes("unavailable") ? "unavailable" :
    sources.every((s) => s === "reported") ? "reported" :
      sources.every((s) => s === "estimated") ? "estimated" : "mixed";

export function readLlmAnswer(call: LlmUsage): boolean | null {
  if (typeof call.response === "boolean") return call.response;
  try {
    const content = typeof call.completion === "string"
      ? JSON.parse(call.completion.replace(/^```(?:json)?\s*|\s*```$/g, ""))
      : call.completion;
    return typeof content?.response === "boolean" ? content.response : null;
  } catch { return null; }
}

export function normalizeLlmCall(call: LlmUsage, index = 0) {
  const inputTokens = nonnegative(call.usage?.promptTokens ?? call.usage?.prompt_tokens);
  const outputTokens = nonnegative(call.usage?.completionTokens ?? call.usage?.completion_tokens);
  let cost = nonnegative(call.usage?.cost);
  let source: CostSource = cost === null ? "unavailable" : "reported";
  const pricing = LLM_PRICING[call.model];
  if (cost === null && pricing && inputTokens !== null && outputTokens !== null) {
    cost = (pricing.input_cost_per_million_tokens * inputTokens +
      pricing.output_cost_per_million_tokens * outputTokens) / 1_000_000;
    source = "estimated";
  }
  const answer = readLlmAnswer(call);
  const p = probability(call.probability);
  return {
    index, model: call.model, attempt: call.attempt ?? null,
    isJev: /(?:^|\/)jev(?:[-/]|$)/i.test(call.model),
    decisionId: call.decisionId ?? call.decision_id ?? null,
    criteriaId: call.criteriaId ?? call.criteria_id ?? null,
    selected: call.selected ?? null,
    fallbackUsed: call.fallbackUsed ?? call.fallback_used ?? null,
    fallbackModel: call.fallbackModel ?? call.fallback_model ?? null,
    fallbackConfidence: probability(call.fallbackConfidence ?? call.fallback_confidence),
    primaryProbability: probability(call.primaryProbability ?? call.primary_probability),
    answer, probability: p,
    confidence: p !== null && answer !== null ? (answer ? p : 1 - p) : null,
    duration: nonnegative(call.durationSeconds ?? call.duration_seconds),
    inputTokens, outputTokens, cost, costSource: source,
  };
}
export type LlmCall = ReturnType<typeof normalizeLlmCall>;

export function getJevDecisions(calls: LlmCall[]) {
  return calls.filter((c) => c.isJev && c.attempt !== "fallback").map((primary) => {
    // Never pair by array position or criteria: a test can make several decisions for one criterion.
    const peers = primary.decisionId ? calls.filter((c) => c.decisionId === primary.decisionId) : [primary];
    const fallbacks = peers.filter((c) => c.attempt === "fallback");
    const ambiguous = peers.filter((c) => c.isJev && c.attempt !== "fallback").length !== 1;
    const route = fallbacks.length || primary.fallbackUsed === true ? "fallback"
      : primary.fallbackUsed === false ? "jev-only" : "unknown";
    const fallback = !ambiguous && fallbacks.length === 1 ? fallbacks[0] : null;
    const agreement = primary.answer !== null && fallback?.answer != null
      ? (primary.answer === fallback.answer ? "agree" : "disagree") : "unknown";
    const complete = !ambiguous && (route === "jev-only" || (route === "fallback" && fallbacks.length > 0));
    const decisionCalls = [primary, ...fallbacks];
    return {
      primary, fallback, route, agreement,
      cost: complete ? sumComplete(decisionCalls.map((c) => c.cost)) : null,
      costSource: complete ? costSource(decisionCalls.map((c) => c.costSource)) : "unavailable" as CostSource,
      duration: complete ? sumComplete(decisionCalls.map((c) => c.duration)) : null,
    };
  });
}
export type JevDecision = ReturnType<typeof getJevDecisions>[number];

export function getLlmUsageStats(result: Pick<EvaluationResults, "llmUsage" | "totalLlmCost">) {
  if (!result.llmUsage && nonnegative(result.totalLlmCost) === null) return undefined;
  const calls = (result.llmUsage ?? []).map(normalizeLlmCall);
  const reported = nonnegative(result.totalLlmCost);
  const totalCost = reported ?? sumComplete(calls.map((c) => c.cost));
  return {
    totalInputTokens: calls.reduce((s, c) => s + (c.inputTokens ?? 0), 0),
    totalOutputTokens: calls.reduce((s, c) => s + (c.outputTokens ?? 0), 0),
    totalCost,
    costSource: reported !== null ? "reported" as CostSource
      : calls.length ? costSource(calls.map((c) => c.costSource)) : "unavailable" as CostSource,
    calls, decisions: getJevDecisions(calls),
  };
}

function metrics(items: { cost: number | null; duration: number | null; costSource: CostSource }[]) {
  const costs = items.filter((i) => i.cost !== null);
  const times = items.filter((i) => i.duration !== null);
  return {
    count: items.length, costSamples: costs.length, durationSamples: times.length,
    estimatedCostSamples: costs.filter((i) => i.costSource !== "reported").length,
    totalCost: costs.length ? costs.reduce((s, i) => s + i.cost!, 0) : null,
    averageCost: costs.length ? costs.reduce((s, i) => s + i.cost!, 0) / costs.length : null,
    averageDuration: times.length ? times.reduce((s, i) => s + i.duration!, 0) / times.length : null,
  };
}
export type UsageMetrics = ReturnType<typeof metrics>;
export type UsageSummaryInput = Pick<EvaluationResults, "llmUsage" | "totalLlmCost" | "duration">;

export function summarizeLlmUsage(results: UsageSummaryInput[]) {
  const evaluations = results.map((r) => ({ result: r, stats: getLlmUsageStats(r) }));
  const calls = evaluations.flatMap((e) => e.stats?.calls ?? []);
  const decisions = evaluations.flatMap((e) => e.stats?.decisions ?? []);
  const groups = ["jev-only", "fallback", "unknown"] as const;
  const summarizeDecisions = (items: JevDecision[]) => ({
    ...metrics(items),
    agree: items.filter((d) => d.agreement === "agree").length,
    disagree: items.filter((d) => d.agreement === "disagree").length,
    unknownAgreement: items.filter((d) => d.route === "fallback" && d.agreement === "unknown").length,
  });
  return {
    version: 1,
    unpairedFallbackCalls: evaluations.reduce((sum, e) => sum + (e.stats?.calls.filter((c) =>
      c.attempt === "fallback" && !e.stats?.decisions.some((d) => d.fallback?.index === c.index)
    ).length ?? 0), 0),
    evaluations: metrics(evaluations.filter((e) => e.stats).map((e) => ({
      cost: e.stats!.totalCost, costSource: e.stats!.costSource, duration: nonnegative(e.result.duration),
    }))),
    models: [...new Set(calls.map((c) => c.model))].sort().map((model) => ({
      model, ...metrics(calls.filter((c) => c.model === model)),
    })),
    routes: groups.map((route) => ({ route, ...summarizeDecisions(decisions.filter((d) => d.route === route)) })),
    // Wall time is per test. Decision time above is the sum of its recorded call durations.
    testRoutes: ["jev-only", "fallback"].map((route) => ({
      route, ...metrics(evaluations.filter((e) => {
        const ds = e.stats?.decisions ?? [];
        return route === "fallback" ? ds.some((d) => d.route === "fallback")
          : ds.length > 0 && ds.every((d) => d.route === "jev-only") && ds.length === e.stats!.calls.length;
      }).map((e) => ({ cost: e.stats!.totalCost, costSource: e.stats!.costSource, duration: nonnegative(e.result.duration) }))),
    })),
    confidenceBuckets: [0, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 0.99].map((lower, i, limits) => ({
      lower, upper: limits[i + 1] ?? 1,
      ...summarizeDecisions(decisions.filter((d) => d.primary.confidence !== null &&
        d.primary.confidence >= lower && (i === limits.length - 1 || d.primary.confidence < limits[i + 1]))),
    })),
    missingConfidence: decisions.filter((d) => d.primary.confidence === null).length,
    // Compact observed samples allow threshold exploration in saved summaries, without storing prompts.
    confidenceSamples: decisions.filter((d) => d.primary.confidence !== null).map((d) => ({
      confidence: d.primary.confidence!, agreement: d.agreement,
    })),
  };
}
export type LlmUsageSummary = ReturnType<typeof summarizeLlmUsage>;

export const formatLlmCost = (cost: number | null) => cost === null ? "Unavailable" : `$${cost.toFixed(8)}`;
export const formatLlmDuration = (seconds: number | null) => seconds === null ? "Unavailable" : `${seconds.toFixed(2)} s`;
export const formatLlmPercent = (value: number | null) => value === null ? "Unavailable" : `${(value * 100).toFixed(1)}%`;
